import { useEffect, useMemo } from "react";
import { CanonicalBusinessEvent } from "../types/cbes";
import { useGlProduct } from "../contexes/GlProductContext";
import { useHealthStateUpdate } from "../contexes/HealthStateContext";
import { isFreeTrial } from "../helpers/freeTrialUtils";
import { calculateCompleteHealthState } from "../helpers/healthUtils";
import {
  deriveOrderDetails,
  getGlMapping,
  SummaryEvent,
  SummaryOrderDetails,
} from "../helpers/summaryDataUtils";
import { DlqInfo } from "./useFetchDlqInfo";
import { MatchSetData } from "./useFetchMatchSets";

export default function useTransactionStateSync(
  eventData: SummaryEvent[],
  cbeData: CanonicalBusinessEvent[],
  dlqInfo: DlqInfo,
  matchSetData: MatchSetData | undefined,
  enabled: boolean,
  orderDetailsOverride?: SummaryOrderDetails
) {
  const { setGlProduct } = useGlProduct();
  const { setState: setHealthState } = useHealthStateUpdate();

  const orderDetails = useMemo(
    () =>
      orderDetailsOverride ??
      (enabled ? deriveOrderDetails(eventData, cbeData) : undefined),
    [cbeData, enabled, eventData, orderDetailsOverride]
  );
  const freeTrialStatus = useMemo(
    () => (enabled ? isFreeTrial(eventData) : false),
    [enabled, eventData]
  );
  const matchSetAllClosed = matchSetData?.allClosed;
  const matchSetOpenCount = matchSetData?.openCount;
  const matchSetTotal = matchSetData?.total;
  const matchSetIsLoading = matchSetData?.isLoading;
  const matchSetIsError = matchSetData?.isError;
  const normalizedDlqInfo = useMemo(
    () => ({
      status: dlqInfo.status,
      dlqItems: dlqInfo.dlqItems,
      error: dlqInfo.error,
    }),
    [dlqInfo.dlqItems, dlqInfo.error, dlqInfo.status]
  );
  const healthMatchSetData = useMemo<MatchSetData | undefined>(() => {
    if (matchSetTotal === undefined) {
      return undefined;
    }

    return {
      matchSets: [],
      allClosed: matchSetAllClosed ?? false,
      openCount: matchSetOpenCount ?? 0,
      total: matchSetTotal,
      isLoading: matchSetIsLoading ?? false,
      isError: matchSetIsError ?? false,
    };
  }, [
    matchSetAllClosed,
    matchSetIsError,
    matchSetIsLoading,
    matchSetOpenCount,
    matchSetTotal,
  ]);
  const nextHealthState = useMemo(() => {
    if (!enabled) {
      return undefined;
    }

    return calculateCompleteHealthState(
      eventData,
      cbeData,
      normalizedDlqInfo,
      undefined,
      freeTrialStatus,
      healthMatchSetData
    );
  }, [
    cbeData,
    enabled,
    eventData,
    freeTrialStatus,
    healthMatchSetData,
    normalizedDlqInfo,
  ]);

  useEffect(() => {
    if (!enabled || !orderDetails) {
      return;
    }

    setGlProduct(
      orderDetails.glProductGroup
        ? getGlMapping(orderDetails.glProductGroup.toString())
        : "unknown"
    );
  }, [enabled, orderDetails, setGlProduct]);

  useEffect(() => {
    if (!enabled || !nextHealthState) {
      return;
    }

    setHealthState(nextHealthState);
  }, [enabled, nextHealthState, setHealthState]);
}
